import dayjs from 'dayjs';
import { oc } from 'ts-optchain';
import isEmpty from 'lodash/isEmpty';
import base64 from 'react-native-base64';
import Config from 'react-native-config';
import { RootDispatch, Thunk } from 'store/types';
import { messageQuestion, messageShare } from 'store/messaging/actions';
import { ISO_8601_DATE } from 'lib/constants';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  Action,
  Clients,
  DFDHeadersObject,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource
} from 'modules/types/common';
import { getFilterOptions } from 'store/testResults/utils';
import { CacheTypes } from 'modules/types/mobileCaching';
import { DFDHeaders } from 'serverTypes/authentication';
import { TransmitSources } from '../transmit';
import {
  TestResult,
  TestResultsFilter,
  DateRangeFilterParams,
  TestResultsList,
  SortByValues,
  TestResultFocused
} from './types';
import { testResultsListRequestStateSelector } from './selectors';
import { INIT_YEARS_TO_QUERY } from './constants';
/**
 * Lab Actions
 */
export * from './lab/actions';

/**
 * Microbiology Actions
 */
export * from './microbiology/actions';

/**
 * Imaging Actions
 */
export * from './imaging/actions';

/**
 * Pathology Actions
 */
export * from './pathology/actions';

export const GET_TEST_RESULT_DETAILS = 'TEST_RESULTS.GET_TEST_RESULT_DETAILS';
export const GET_TEST_RESULT_DETAILS_SUCCESS = 'TEST_RESULTS.GET_TEST_RESULT_DETAILS_SUCCESS';
export const GET_TEST_RESULT_DETAILS_FAIL = 'TEST_RESULTS.GET_TEST_RESULT_DETAILS_FAIL';

export interface GetTestResultDetails {
  contentType?: string;
  url: string;
  responseType?: string;
  headers?: DFDHeadersObject;
}

export const getTestResultDetails = (
  { url, contentType, responseType = '', headers = {} }: GetTestResultDetails,
  testResult: TestResult | TestResultFocused | undefined = undefined
) => {
  let client = 'testResults';
  headers = {
    [DFDHeaders.mhpAccept]: contentType,
    ...headers
  };

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';

    if (testResult?.mhpDetails?.url) {
      // eslint-disable-next-line prefer-destructuring
      url = testResult.mhpDetails.url;
    }

    if (!!testResult?.mhpDetails && !isEmpty(testResult?.mhpDetails?.headers)) {
      headers = {
        ...testResult.mhpDetails.headers
      };
    }
  } else if (!!testResult && !isEmpty(testResult)) {
    headers[DFDHeaders.auditData] = base64.encode(
      JSON.stringify({
        date: testResult.testDate,
        message: testResult.name
      })
    );
  }

  return {
    type: GET_TEST_RESULT_DETAILS,
    payload: {
      client,
      request: {
        method: 'get',
        url,
        headers,
        responseType
      }
    }
  };
};

export const RESET_TEST_RESULT_DETAILS = 'TEST_RESULTS.RESET_TEST_RESULT_DETAILS';

export const resetTestResultDetails = () => ({ type: RESET_TEST_RESULT_DETAILS });

/**
 * Test Results Reply
 */
export const createReplyBodyFromTestResults = (testResult: TestResult | TestResultFocused) => {
  // Test Result Information
  const encounterNumber = testResult.encounterId;
  const testDate = dayjs(testResult.testDate).format('MM/DD/YYYY');
  const testName = testResult.name;
  const testCategory = testResult.category;
  const testSource = testResult.source;
  const getProviders = oc(testResult).providerNames([]);
  const providers = getProviders.length ? getProviders.join(', ') : 'N/A';

  const replyBody = `Encounter Number: ${encounterNumber}\n\nLab Name: ${testName}\n\nLab Date: ${testDate}\n\nTest Category: ${testCategory}\n\nTest Source: ${testSource}\n\nProviders: ${providers}`;
  return replyBody;
};

export interface TestResultsQuestionProps {
  testResult: TestResult | TestResultFocused;
  subject?: string;
  onSendSuccess?: () => void;
  config?: {
    lockRecipient?: boolean;
    lockSubject?: boolean;
    hideMessage?: boolean;
  };
}

export const askTestResultsQuestion = (
  props: TestResultsQuestionProps,
  isShare = false,
  returnBackTo?: string
): Thunk => dispatch => {
  const eventId = () => {
    if (props.testResult.category === 'Lab' && props.testResult.source === 'MILLENNIUM') {
      const pipedIds = props.testResult.componentEventIds
        .map(componentEventId => componentEventId)
        .join('|');

      return pipedIds;
    }

    return props.testResult.eventId;
  };

  let source;
  switch (props.testResult.category) {
    case 'Lab':
      source = TransmitSources.LabResults;
      break;
    case 'Microbiology':
      source = TransmitSources.MicrobiologyResults;
      break;
    case 'Imaging':
      source = TransmitSources.ImagingResults;
      break;
    case 'Pathology':
      source = TransmitSources.PathologyResults;
      break;
    default:
      return source;
  }

  const messageParams = {
    onSendSuccess: props.onSendSuccess,
    subject: 'Test Results Question',
    readonly: createReplyBodyFromTestResults(props.testResult),
    transmitParams: {
      source,
      payload: {
        eventId: eventId(),
        source: props.testResult.source,
        name: props.testResult?.name,
        testDate: props.testResult.testDate
      },
      returnBackTo
    },
    ...props.config
  };

  if (isShare) {
    return dispatch(messageShare(messageParams));
  }

  return dispatch(messageQuestion(messageParams));
};

export const GET_TEST_RESULTS_LIST = 'TEST_RESULTS.GET_TEST_RESULTS_LIST';
export const GET_TEST_RESULTS_LIST_FAIL = 'TEST_RESULTS.GET_TEST_RESULTS_LIST_FAIL';
export const GET_TEST_RESULTS_LIST_SUCCESS = 'TEST_RESULTS.GET_TEST_RESULTS_LIST_SUCCESS';
export const SET_TEST_RESULTS_LIST_LAST_UPDATED = 'TEST_RESULTS.SET_TEST_RESULTS_LIST_LAST_UPDATED';

export const UPDATE_SORT = 'TEST_RESULTS.UPDATE_SORT';
export const updateSort = (sortBy: string) => ({
  type: UPDATE_SORT,
  payload: { sortBy }
});

export const UPDATE_FILTER_OPTIONS = 'TEST_RESULTS.UPDATE_FILTER_OPTIONS';
export const updateFilterOptions = (filterOptions: TestResultsFilter[]) => ({
  type: UPDATE_FILTER_OPTIONS,
  payload: { filterOptions }
});

export const UPDATE_DATE_RANGE = 'TEST_RESULTS.UPDATE_DATE_RANGE';
export const updateDateRange = (dateRangeParams: DateRangeFilterParams) => ({
  type: UPDATE_DATE_RANGE,
  payload: dateRangeParams
});

export const APPLY_TEST_RESULTS_FILTERS = 'TEST_RESULTS.APPLY_TEST_RESULTS_FILTERS';
export const RESET_TEST_RESULTS_FILTERS = 'TEST_RESULTS.RESET_TEST_RESTULS_FILTERS';
export const applyTestResultsFilters = () => ({ type: APPLY_TEST_RESULTS_FILTERS });
export const resetTestResultsFilters = () => ({ type: RESET_TEST_RESULTS_FILTERS });
export const GET_TEST_RESULTS_LIST_CACHE_SUCCESS =
  'TEST_RESULTS.GET_TEST_RESULTS_LIST_CACHE_SUCCESS';

export const GET_MORE_TEST_RESULTS_LIST = 'TEST_RESULTS.GET_MORE_TEST_RESULTS_LIST';

function getTestResultsListAction({
  useServerCache,
  useRequestState,
  startDate,
  endDate,
  type = GET_TEST_RESULTS_LIST
}: {
  useServerCache?: boolean;
  useRequestState?: boolean;
  startDate: string;
  endDate: string;
  type?: string;
}): Action {
  let client: Clients = 'testResults';
  let url = `list?includeEvent=true&startDate=${startDate}&endDate=${endDate}`;

  if (!!Config.TEST_RESULTS_EXPANSION_ENABLED) {
    client = 'dispatcher';
    url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.MEDICAL_TEST_RESULTS,
      `?startDate=${startDate}&endDate=${endDate}`
    );
  }

  return {
    type,
    payload: {
      client,
      useRequestState,
      requestStateSelector: testResultsListRequestStateSelector,
      cacheType: CacheTypes.TEST_RESULTS,
      cacheRetrievalSuccessAction: GET_TEST_RESULTS_LIST_CACHE_SUCCESS,
      request: {
        url,
        headers: {
          [DFDHeaders.cacheControl]: useRequestState || useServerCache ? 'use-cache' : 'no-cache',
          [DFDHeaders.mhpCacheControl]: useRequestState || useServerCache ? 'use-cache' : 'no-cache'
        }
      }
    }
  };
}

/**
 * Method: getMoreTestResults
 * Description: Fetch more list without clearing the list.
 */
export const getMoreTestResults = (startDate: string | Date, endDate: string | Date) => async (
  dispatch: RootDispatch
) => {
  // get list of
  const listRes: Action<TestResultsList> = await dispatch(
    getTestResultsListAction({
      startDate: dayjs(startDate).format(ISO_8601_DATE),
      endDate: dayjs(endDate).format(ISO_8601_DATE),
      type: GET_MORE_TEST_RESULTS_LIST,
      useServerCache: true
    })
  );
  dispatch(resetTestResultsFilters());
  dispatch(updateSort(SortByValues.desc));
  if (listRes.errors || listRes.payload?.errors) {
    return dispatch({ type: GET_TEST_RESULTS_LIST_FAIL, payload: listRes.payload });
  }

  const options = getFilterOptions(listRes?.payload?.data?.results as TestResult[]);
  dispatch(updateFilterOptions(options));
  return dispatch({ type: GET_TEST_RESULTS_LIST_SUCCESS, payload: listRes.payload });
};

export function getTestResultsList(
  startDate?: string,
  endDate?: string,
  useRequestState = true
): Action {
  startDate =
    startDate ||
    dayjs()
      .subtract(INIT_YEARS_TO_QUERY, 'year')
      .format(ISO_8601_DATE);

  endDate = endDate || dayjs().format(ISO_8601_DATE);
  return getTestResultsListAction({ useRequestState, startDate, endDate });
}

export const GET_TEST_RESULT_LIST_BY_ENCOUNTER = 'TEST_RESULTS.GET_TEST_RESULT_LIST_BY_ENCOUNTER';
export const GET_TEST_RESULT_LIST_BY_ENCOUNTER_FAIL =
  'TEST_RESULTS.GET_TEST_RESULT_LIST_BY_ENCOUNTER_FAIL';
export const GET_TEST_RESULT_LIST_BY_ENCOUNTER_SUCCESS =
  'TEST_RESULTS.GET_TEST_RESULT_LIST_BY_ENCOUNTER_SUCCESS';

export const getTestResultListByEncounter = ({ encounterId }: { encounterId: string }): Action => ({
  type: GET_TEST_RESULT_LIST_BY_ENCOUNTER,
  payload: {
    client: 'dispatcher',
    request: {
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.MEDICAL_TEST_RESULTS
      ),
      headers: {
        [DFDHeaders.mhpFilterEncounterId]: encounterId
      }
    }
  }
});

export const GET_LAB_WITH_DETAILS = 'TEST_RESULTS.GET_LAB_WITH_DETAILS';
export const GET_LAB_WITH_DETAILS_FAIL = 'TEST_RESULTS.GET_LAB_WITH_DETAILS_FAIL';
export const GET_LAB_WITH_DETAILS_SUCCESS = 'TEST_RESULTS.GET_LAB_WITH_DETAILS_SUCCESS';

interface GetLabWithDetailsOptions {
  acceptType?: string;
  encounterId?: string;
  source?: string;
  testResultDate: string;
}
// sourceIds = id|id|id|id
export const getLabWithDetails = (
  sourceIds: string,
  options?: GetLabWithDetailsOptions
): Action => {
  const { acceptType, ...data } = options || ({} as GetLabWithDetailsOptions);
  return {
    type: GET_LAB_WITH_DETAILS,
    payload: {
      client: 'testResults',
      request: {
        url: `lab/test-result/${sourceIds}`,
        data,
        headers: {
          'cache-control': 'max-age=3600',
          Accept: acceptType || 'application/json' // NOTE: you can ask for a different type, but 99% of the time you will want application/json
        }
      }
    }
  };
};
